import { DOCUMENT } from '@angular/core';
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@core/constants/window-token';
import { environment } from '@environment/environment';
import { SdkInitResponse } from 'src/app/mosaic/models/sdk_interfaces';

@Injectable({ providedIn: 'root' })
export class MosaicService {
  private readonly SCRIPT_ID: string = 'ts-platform-script';
  private initialized: boolean = false;
  private inProgressInitPromise: Promise<SdkInitResponse> | null = null;
  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) private windowRef: Window,
  ) {}

  public loadSDkMosaic(): Promise<SdkInitResponse> {
    if (this.initialized) {
      return Promise.resolve({ id: this.SCRIPT_ID, status: 'SDK Mosaic already load' });
    }

    if (this.inProgressInitPromise) {
      return this.inProgressInitPromise;
    }

    this.inProgressInitPromise = this.loadAndInitialize().finally(() => {
      if (!this.initialized) {
        this.inProgressInitPromise = null;
      }
    });

    return this.inProgressInitPromise;
  }

  private loadAndInitialize(): Promise<SdkInitResponse> {
    return new Promise<SdkInitResponse>((resolve, reject) => {
      const hasScript = this.document.getElementById(this.SCRIPT_ID) as HTMLScriptElement | null;

      if (hasScript) {
        // Si el script ya está en DOM, solo inicializamos cuando tsPlatform realmente exista.
        if (this.windowRef.tsPlatform && this.windowRef.tsPlatform.initialize) {
          try {
            this.initializeMosaicSDK();
            this.initialized = true;
            resolve({ id: this.SCRIPT_ID, status: 'SDK Mosaic initialized' });
          } catch (error) {
            reject({ id: this.SCRIPT_ID, status: 'Error initializing SDK Mosaic', details: error });
          }
          return;
        }

        const onLoad = () => {
          hasScript.removeEventListener('load', onLoad);
          hasScript.removeEventListener('error', onError);
          try {
            this.initializeMosaicSDK();
            this.initialized = true;
            resolve({ id: this.SCRIPT_ID, status: 'SDK Mosaic initialized' });
          } catch (error) {
            reject({ id: this.SCRIPT_ID, status: 'Error initializing SDK Mosaic', details: error });
          }
        };

        const onError = (error: Event) => {
          hasScript.removeEventListener('load', onLoad);
          hasScript.removeEventListener('error', onError);
          console.error('Error loading SDK Mosaic');
          reject({ id: this.SCRIPT_ID, status: 'Error loading script Mosaic', details: error });
        };

        hasScript.addEventListener('load', onLoad, { once: true });
        hasScript.addEventListener('error', onError, { once: true });
        return;
      }

      const scriptSDK = this.document.createElement('script');
      scriptSDK.type = 'text/javascript';
      scriptSDK.id = this.SCRIPT_ID;
      scriptSDK.src = environment.mosaic.sdkUrl;
      scriptSDK.defer = true;

      scriptSDK.onload = () => {
        try {
          this.initializeMosaicSDK();
          this.initialized = true;
          resolve({ id: this.SCRIPT_ID, status: 'SDK Mosaic initialized' });
        } catch (error) {
          reject({ id: this.SCRIPT_ID, status: 'Error initializing SDK Mosaic', details: error });
        }
      };

      scriptSDK.onerror = (error) => {
        console.error('Error loading SDK Mosaic');
        reject({ id: this.SCRIPT_ID, status: 'Error loading script Mosaic', details: error });
      };

      this.document.head.appendChild(scriptSDK);
    });
  }

  private initializeMosaicSDK(): void {
    if (!this.windowRef.tsPlatform || !this.windowRef.tsPlatform.initialize) {
      throw new Error('window.tsPlatform is not available');
    }

    // console.log('Initializing SDK Mosaic...');
    this.windowRef.tsPlatform.initialize({
      clientId: environment.mosaic.clientId,
      drs: {
        serverPath: environment.mosaic.drsServerUrl,
        enableSessionToken: false,
      },
      ido: { serverPath: environment.mosaic.idoServerUrl },
    });
    //console.log('Transmit Security SDK initialized successfully');
  }

  public removeScriptMosaic(): void {
    const scriptSDK = this.document.getElementById(this.SCRIPT_ID);
    if (scriptSDK) {
      scriptSDK.remove();
      this.initialized = false;
      //console.log('SDK Mosaic script successfully removed');
    }
  }
}
